import { Suspense, useState } from 'react';
import { useTracking } from 'react-tracking';

import loadable from '@loadable/component';
import { NarrativeFairValueActionSheet } from './components/NarrativeFairValueActionSheet';
import { ValuationSelectionForm } from './components/ValuationSelectionForm';
import { Alert, AlertAction, Button } from '@simplywallst/ui-core';
import { ErrorBoundary } from '@sentry/react';
import { NarrativeFairValueHeader } from './components/NarrativeFairValueHeader';
import { NarrativeCardLoading } from '@/components/NarrativeCard';

const NarrativeOnboardingModal = loadable(
  () => import('@components/NarrativeOnboardingModal')
);

export interface Props {
  companyId: string;
  isModalOpen: boolean;
  onClose: () => void;
  userHoldsPosition?: boolean;
}

export const NarrativeFairValueModal = ({
  companyId,
  isModalOpen,
  onClose,
  userHoldsPosition,
}: Props) => {
  const [showingOnboarding, setShowingOnboarding] = useState(false);

  const { Track } = useTracking({
    type: 'track',
    subject: 'FairValue',
    companyId,
  });

  if (showingOnboarding)
    return (
      <Track>
        <NarrativeOnboardingModal
          companyId={companyId}
          isOpen={showingOnboarding}
          onClose={() => setShowingOnboarding(false)}
        />
      </Track>
    );

  return (
    <Track>
      <NarrativeFairValueActionSheet isOpen={isModalOpen} onClose={onClose}>
        <ErrorBoundary
          fallback={
            <div className="grid gap-4 grid-rows-[max-content_max-content]">
              <NarrativeFairValueHeader>Valuation</NarrativeFairValueHeader>
              <div className="px-6">
                <Alert
                  styleType="negative"
                  actions={
                    <AlertAction onClick={() => window.location.reload()}>
                      Reload
                    </AlertAction>
                  }
                >
                  Failed to load narratives and fair value. Please try again.
                </Alert>
              </div>
            </div>
          }
        >
          <Suspense
            fallback={
              <div
                className="grid overflow-hidden h-full grid-rows-[max-content_1fr_max-content]"
                role="presentation"
                aria-label="Loading skeleton"
              >
                <NarrativeFairValueHeader>Valuation</NarrativeFairValueHeader>
                <div className="relative overflow-x-hidden overflow-y-auto h-full scrollbar">
                  <div className="min-[375px]:px-x2 lg:px-x4 py-6 grid gap-2 grid-cols-1 sm:grid-cols-2 grid-rows-[max-content_max-content]">
                    <NarrativeCardLoading />
                    <NarrativeCardLoading />
                  </div>
                </div>
                <div className="grid grid-cols-1 py-4 pr-x2 lg:px-x4 justify-items-end bg-surface">
                  <Button
                    styleType="louderLarge"
                    onClick={onClose}
                    data-cy-id="narrative-fair-value-modal-done"
                  >
                    Done
                  </Button>
                </div>
              </div>
            }
          >
            {isModalOpen && (
              <ValuationSelectionForm
                onClose={onClose}
                companyId={companyId}
                openOnboardingFlow={() => setShowingOnboarding(true)}
                userHoldsPosition={userHoldsPosition}
              />
            )}
          </Suspense>
        </ErrorBoundary>
      </NarrativeFairValueActionSheet>
    </Track>
  );
};
